import { DateTime } from 'luxon';
import type { SagaIterator } from 'redux-saga';
import { call, cancelled, delay, put, select, take, takeEvery } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { a2aTransfersActions } from 'app-components/flux/a2aTransfers';
import { accountsActions, accountsSelectors, accountsTypes } from 'app-components/flux/accounts';
import { authSelectors } from 'app-components/flux/auth';
import { getBusinessForId } from 'app-components/flux/business/businessSelectors';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { resourceSagaUtils } from 'app-components/flux/core';
import {
  type BaseResource,
  createTypedQcsSyncResourcesConfigFactory,
  type QcsSyncResourcesConfigProps,
} from 'app-components/flux/core/resourceStoreTypes';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import * as merchantOrdersActions from 'app-components/flux/merchantOrders/merchantOrdersActions';
import { getLogger, httpClientInterface, requireEnvironmentConfig } from 'app-components/utils/core';
import { getQcsDeletedResourceId } from 'app-components/utils/core/httpErrors';

import * as actions from './transactionsActions';
import * as transactionsSelectors from './transactionsSelectors'; // , getTransactionById TODO: see performTxnAction note
import * as transformers from './transactionsTransformers';
import {
  type CashFlowTransaction,
  SplitItemType,
  type TransactionProps,
  type TransactionRecord,
} from './transactionsTypes';

const log = getLogger('app-components/flux/transactions/transactionsSagas');

const mkQcsSyncTransactionsConfig = createTypedQcsSyncResourcesConfigFactory<TransactionProps>();
const mkTransactionActionConfig = createTypedQcsSyncResourcesConfigFactory<
  TransactionProps,
  actions.TransactionActionPayload
>();

// ====================================================
// PERFORM TRANSACTION ACTION
// a saga for utilizing the transactions/{id}/action or transactions/{id}/action/{otherId}
// pass in an action api object
//
// There is a potential for QCS to auto-match and delete/accept upcoming instances if the user happens to be doing this action
// during a refresh. While we can't completely prevent this from causing errors, we SHOULD check to make sure any respective txns still
// exist client-side before telling QCS to do something with them.
//
// {
//   id: txnId to impact
//   action: 'ACCEPT' || 'SKIP' || 'MATCH' || 'UNMATCH'   See QCS Documentation for updates
//   otherId: if two IDs are involved in action (e.g. match)
// }
//
type SkipWaiter = { resolve?(result: unknown): void; reject?(error: unknown): void };
/** Promise callbacks of every dismiss waiting on the skip in flight, by reminder id. One per watcher. */
export type SkipsInFlight = Map<string, SkipWaiter[]>;

/** A dismiss of a reminder QCS already deleted leaves it gone, which is what the user asked for. */
const isAlreadyDeletedSkip = (payload: actions.TransactionActionPayload, error: unknown) =>
  payload.action === 'skip' && getQcsDeletedResourceId(error) === payload.id;

// A repeat dismiss sent before the first skip returns would reach QCS after the reminder is deleted and
// fail with QCS-0400-5, so it is not sent: it waits for the skip in flight and settles the same way.
export function* performTransactionAction(
  skipsInFlight: SkipsInFlight,
  actionData: ReturnType<typeof actions.performTransactionAction>,
): SagaIterator<null> {
  const { payload, meta } = actionData;
  if (payload.action !== 'skip') {
    yield call(performTransactionActionRequest, actionData);
    return null;
  }

  const waiter: SkipWaiter = { resolve: meta?.resolve, reject: meta?.reject };
  const waiters = skipsInFlight.get(payload.id);
  if (waiters) {
    waiters.push(waiter);
    return null;
  }

  const ownWaiters = [waiter];
  skipsInFlight.set(payload.id, ownWaiters);
  try {
    yield call(performTransactionActionRequest, {
      ...actionData,
      meta: {
        ...meta,
        resolve: (result: unknown) => ownWaiters.forEach((each) => each.resolve?.(result)),
        reject: (error: unknown) =>
          isAlreadyDeletedSkip(payload, error)
            ? ownWaiters.forEach((each) => each.resolve?.(undefined))
            : ownWaiters.forEach((each) => each.reject?.(error)),
      },
    });
  } finally {
    skipsInFlight.delete(payload.id);
  }
  return null;
}

function* performTransactionActionRequest(
  actionData: ReturnType<typeof actions.performTransactionAction>,
): SagaIterator<void> {
  // TODO: once mobile migrates txns to redux, consider adding this check to prevent match failures and whatnot
  // // make sure firstTxn still exists before performing
  // const firstId = actionData?.payload?.id;
  // if (!firstId) {
  //   return null;
  // }
  // const firstTxn = yield select((store) => getTransactionById(store, firstId));
  // if (!firstTxn) {
  //   return null;
  // }
  //
  // const secondId = actionData?.payload?.otherId;
  // if (secondId) { // if secondId, make sure second txn still exists before performing
  //   const secondTxn = yield select((store) => getTransactionById(store, secondId));
  //   if (!secondTxn) {
  //     return null;
  //   }
  // }

  const config = mkTransactionActionConfig({
    resourceName: 'transaction action',
    resourceBaseUrl: (action: ReturnType<typeof actions.performTransactionAction>) =>
      `/transactions/${action.payload.id}/${action.payload.action.toLowerCase()}${
        action.payload.otherId ? `/${action.payload.otherId}` : ''
      }`,
    successAction: actions.performTransactionActionSuccess,
    failureAction: actions.performTransactionActionFailure,
    // Nothing to report for a reminder that is already gone; the failure reducer removes the stale row.
    ignoreError: (error) => isAlreadyDeletedSkip(actionData.payload, error),
    getLastSyncDate: () => null,
    transformResourceToRequestData: (resource) => {
      const { ...other } = resource;
      return { ...other };
    },
    transformResourceWithResponse: (_resource, response) => {
      if (response?.data?.resources) {
        return transformers.transformQcsTransactionsToTransactions(response.data.resources);
      }
      if (response.data?.resource) {
        return transformers.transformQcsTransactionToTransaction(response.data.resource);
      }
      return null;
    },
  });

  yield call(
    resourceSagaUtils.qcsSyncUpdateResource<TransactionProps, actions.TransactionActionPayload>,
    config,
    actionData,
  );
}

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('transactionsDumpUpdater');
export function* transactionsDumpUpdater(resources: Array<TransactionProps>) {
  yield callFirst(transactionsDumpUpdaterFull, resources);
  yield callLast(transactionsNudgeUpdater);
}

function* transactionsDumpUpdaterFull(resources: Array<TransactionProps>): SagaIterator {
  const process: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpProcessTxnUpdates');
  if (process) {
    yield put(
      actions.applyTransactionsChanges(transformers.transformQcsTransactionsToTransactions(resources), {
        context: 'BUMP_CHANGE_DUMP',
      }),
    );
  }
}

export function* transactionsNudgeUpdater(): SagaIterator {
  const process = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpProcessTxnUpdates');
  if (process) {
    yield put(actions.getTransactions(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

export const mkResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<TransactionProps>>) =>
  mkQcsSyncTransactionsConfig({
    resourceName: 'transaction',
    resourceBaseUrl: '/transactions',
    getLastSyncDate: transactionsSelectors.getLastSyncDate,
    transformResourceWithResponse: transformers.transformResourceWithResponse,
    transformResourcesWithResponse: transformers.transformResourcesWithResponse,
    transformResponseToResources: transformers.transformResponseToTransactions,
    transformResourceToRequestData: transformers.transformTransactionToRequestData,
    transformResourcesToRequestData: transformers.transformTransactionsToRequestData,
    ...props,
  });

export function* getTransactionsActionWatcher(): SagaIterator {
  let dateOnAfterTmp: string | null | undefined;
  let fetching = false;
  let pendingCatchUp:
    | { action: ReturnType<typeof actions.getTransactions>; datasetId: ReturnType<typeof authSelectors.getDatasetId> }
    | undefined;

  function* fetchTransactions(action: ReturnType<typeof actions.getTransactions>): SagaIterator {
    dateOnAfterTmp = action.meta?.props?.dateOnAfter !== undefined ? action.meta.props.dateOnAfter : dateOnAfterTmp;
    const dateOnAfterDB: string | undefined = yield select(transactionsSelectors.getDateOnAfter);
    const defaultDateOnAfter: string | undefined = yield select(transactionsSelectors.getDefaultDateOnAfter);
    const dateOnAfter = dateOnAfterTmp || defaultDateOnAfter;
    const goalAccountIds = yield select(accountsSelectors.getGoalAccountIds);
    const lastSyncDate: string | null | undefined = yield select(transactionsSelectors.getLastSyncDate);
    const loadingOlderHistory = Boolean(dateOnAfter && dateOnAfterDB && dateOnAfter < dateOnAfterDB);
    let dateOnBefore;
    if (loadingOlderHistory && dateOnAfterDB) {
      dateOnBefore = DateTime.fromISO(dateOnAfterDB).plus({ days: 1 }).toISODate(); // setup single chunk load
    }
    const resourceConfig = mkResourceConfig({
      successAction: actions.getTransactionsSuccess,
      failureAction: actions.getTransactionsFailure,
      ...((dateOnAfter || dateOnBefore) && { axiosConfig: { params: { dateOnAfter, dateOnBefore } } }),
      ...(loadingOlderHistory && { getLastSyncDate: () => null }), // setup single chunk load
    });
    yield call(resourceSagaUtils.qcsSyncGetResources<TransactionProps>, resourceConfig, {
      ...action,
      meta: {
        ...action.meta,
        props: {
          ...(action.meta?.props || {}),
          goalAccountIds: goalAccountIds, // inject goalAccountIds into meta props for reducer filtering
          incremental: !loadingOlderHistory && Boolean(lastSyncDate),
        },
      },
    });
  }

  yield takeEvery(actions.getTransactions, function* getTransactions(action): SagaIterator {
    if (fetching) {
      // A suppressed dump depends on this nudge for its only catch-up, and a failed or queued write
      // on its reconcile fetch to undo the optimistic update. Preserve one trailing request when an
      // older fetch is still running; ordinary overlapping GETs stay coalesced.
      const context = action.meta?.context;
      if (context === 'BUMP_CHANGE_NUDGE' || context === actions.RECONCILE_WRITE_CONTEXT) {
        pendingCatchUp = { action, datasetId: yield select(authSelectors.getDatasetId) };
      }
      return;
    }

    fetching = true;
    try {
      yield call(fetchTransactions, action);
    } finally {
      fetching = false;
      if (yield cancelled()) pendingCatchUp = undefined;
    }

    const queued = pendingCatchUp;
    pendingCatchUp = undefined;
    if (queued) {
      const datasetId: ReturnType<typeof authSelectors.getDatasetId> = yield select(authSelectors.getDatasetId);
      // Redispatch so the reducer marks the trailing request as loading again. A cancelled
      // worker never reaches this point; an old dataset's pending work is discarded.
      if (queued.datasetId === datasetId) yield put(queued.action);
    }
  });
}

/** Fetch transactions after an account refresh, then run work that needs the updated register. */
export function* postRefreshTransactionsSaga(action: ReturnType<typeof actions.postRefreshTransactions>): SagaIterator {
  yield put(actions.getTransactions(undefined, action.meta));

  const result: ReturnType<typeof actions.getTransactionsSuccess> | ReturnType<typeof actions.getTransactionsFailure> =
    yield take([actions.getTransactionsSuccess, actions.getTransactionsFailure]);
  if (actions.getTransactionsSuccess.match(result)) {
    yield put(merchantOrdersActions.fetchAndProcessMerchantOrders());
  }
}

export function* postRefreshTransactionsWatcher(): SagaIterator {
  yield takeEvery(actions.postRefreshTransactions, postRefreshTransactionsSaga);
}

export function* getTransactionActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.getTransactionSuccess,
    failureAction: actions.getTransactionFailure,
    getLastSyncDate: () => null,
    transformResponseToResource: (response) => transformers.transformQcsTransactionToTransaction(response.data),
  });
  yield takeEvery(actions.getTransaction, function* getTransaction(action): SagaIterator {
    yield call(resourceSagaUtils.qcsSyncGetResource<TransactionProps>, resourceConfig, action);
  });
}

export function* createTransactionActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.createTransactionSuccess,
    failureAction: actions.createTransactionFailure,
  });
  yield takeEvery(actions.createTransaction, function* createTransaction(action): SagaIterator {
    let txn = action.payload;
    try {
      if (txn.type === 'INVOICE') {
        // create A/R account if required
        txn = yield call(handleAccountsReceivableAccount, txn);

        // create sales-tax account if required
        txn = yield call(handleSalesTaxAccount, txn);
      }
    } catch (error) {
      yield put(actions.createTransactionFailure(error));
      action.meta?.reject?.(error);
      return;
    }
    // create transaction
    yield call(resourceSagaUtils.qcsSyncCreateResource<TransactionProps>, resourceConfig, {
      ...action,
      payload: txn,
    });
  });
}

export function* updateTransactionActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.updateTransactionSuccess,
    failureAction: actions.updateTransactionFailure,
  });
  yield takeEvery(actions.updateTransaction, function* updateTransaction(action): SagaIterator {
    let txn = action.payload;
    try {
      if (txn.type === 'INVOICE') {
        // create A/R account if required
        txn = yield call(handleAccountsReceivableAccount, txn);

        // create sales-tax account if required
        txn = yield call(handleSalesTaxAccount, txn);
      }
    } catch (error) {
      yield put(actions.updateTransactionFailure(error));
      action.meta?.reject?.(error);
      return;
    }
    // create transaction
    yield call(resourceSagaUtils.qcsSyncUpdateResource<TransactionProps>, resourceConfig, {
      ...action,
      payload: txn,
    });
  });
}

export function* deleteTransactionActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.deleteTransactionSuccess,
    failureAction: actions.deleteTransactionFailure,
  });
  yield takeEvery(
    actions.deleteTransaction.type,
    resourceSagaUtils.qcsSyncDeleteResource<TransactionProps>,
    resourceConfig,
  );
}

// Bulk edits over a whole register can reach several thousand transactions, which exceeds the
// service timeout as one request. 1000 matches the chunk size the Mint importer already sends to
// this endpoint.
const MAX_TRANSACTIONS_PER_BATCH_REQUEST = 1000;

export function* batchTransactionsActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.batchTransactionsSuccess,
    failureAction: actions.batchTransactionsFailure,
    maxResourcesPerRequest: MAX_TRANSACTIONS_PER_BATCH_REQUEST,
  });
  yield takeEvery(
    actions.batchTransactions.type,
    resourceSagaUtils.qcsSyncUpdateResources<TransactionProps>,
    resourceConfig,
  );
}

type CreatedAccountResult =
  | ReturnType<typeof accountsActions.createAccountSuccess>
  | ReturnType<typeof accountsActions.createAccountFailure>;

/** Waits for the outcome of the account this preflight requested, not another concurrent creation. */
function* takeCreatedAccountResult(clientId: string): SagaIterator<CreatedAccountResult> {
  const result: CreatedAccountResult = yield take((action: { type: string; payload?: unknown }): boolean => {
    if (!accountsActions.createAccountSuccess.match(action) && !accountsActions.createAccountFailure.match(action)) {
      return false;
    }
    const resultClientId = (action.payload as { clientId?: string | null } | null | undefined)?.clientId;
    // A result without a clientId cannot be correlated, so accept it as before rather than wait forever.
    return resultClientId == null || resultClientId === clientId;
  });
  return result;
}

export function* handleAccountsReceivableAccount(txn: TransactionRecord): SagaIterator {
  if (txn.accountId) return txn;

  const businessId = txn.businessId;
  assert(businessId, 'Invoice must have a businessId');

  // create A/R account if required
  let accountsReceivable = (yield select(accountsSelectors.getArAccountByBusiness))?.get(businessId);
  if (!accountsReceivable) {
    const business = yield select(getBusinessForId, businessId!);
    const clientId = uuid().toUpperCase();
    yield put(
      accountsActions.createAccount(
        accountsTypes.mkAccountsReceivable({
          clientId,
          name: 'Client Invoices' + (business?.name ? ` - ${business.name}` : ''),
          businessId: businessId!,
          usageType: 'BUSINESS',
        }),
      ),
    );
    const result: CreatedAccountResult = yield call(takeCreatedAccountResult, clientId);
    if (accountsActions.createAccountFailure.match(result)) throw result.payload;
    accountsReceivable = result.payload;
  }
  txn = txn.set('accountId', accountsReceivable.id);
  return txn;
}

export function* handleSalesTaxAccount(txn: TransactionRecord): SagaIterator {
  const businessId = txn.businessId;
  assert(businessId, 'Invoice must have a businessId');

  // create sales-tax account if required
  let salesTaxAccount = (yield select(accountsSelectors.getSalesTaxAccountsByBusiness))?.get(businessId);
  if (!salesTaxAccount) {
    // Locate any split item in the transaction of type INVOICE_TAX
    const salesTaxSplitItem = txn.split?.items?.find((item) => item.type === SplitItemType.INVOICE_TAX);

    // If there's a split item of type INVOICE_TAX, create a sales-tax account
    if (salesTaxSplitItem) {
      const business = yield select(getBusinessForId, businessId!);
      const clientId = uuid().toUpperCase();
      yield put(
        accountsActions.createAccount(
          accountsTypes.mkOtherLiabilityAccount({
            subType: 'SALES_TAX',
            clientId,
            name: 'Sales Tax' + (business?.name ? ` - ${business.name}` : ''),
            businessId: businessId!,
            usageType: 'BUSINESS',
          }),
        ),
      );

      // Wait for the sales-tax account creation to succeed and retrieve the created account details
      const result: CreatedAccountResult = yield call(takeCreatedAccountResult, clientId);
      if (accountsActions.createAccountFailure.match(result)) throw result.payload;
      salesTaxAccount = result.payload;

      // Update the split item with the sales-tax account ID
      const coa = chartOfAccountsTypes.mkChartOfAccount({ type: 'ACCOUNT', id: salesTaxAccount.id });
      txn = txn.setIn(
        // since salesTaxSplitItem came from find, ok to cast as any
        ['split', 'items', txn.split?.items?.indexOf(salesTaxSplitItem as any), 'coa'],
        coa,
      );
    }
  }
  return txn;
}

export function* importAppleWalletTransactionsActionWatcher() {
  yield takeEvery(
    actions.importAppleWalletTransactions,
    function* importAppleWalletTransactions(
      action: ReturnType<typeof actions.importAppleWalletTransactions>,
    ): SagaIterator {
      const { payload, meta } = action;

      try {
        const requestUrl = `${httpClientInterface.getServiceUrl()}/pfm-importer/apple-wallet/import`;
        const requestBody = transformers.transformTransactionsToRequestData(payload);
        const { data } = yield call(httpClientInterface.send, 'POST', requestUrl, requestBody);
        if (meta?.resolve && typeof meta.resolve == 'function') {
          yield call(meta.resolve, data);
        }
      } catch (error) {
        if (meta?.reject && typeof meta.reject == 'function') {
          yield call(meta.reject, error);
        }
      }
    },
  );
}

export function* performTransactionActionWatcher() {
  const skipsInFlight: SkipsInFlight = new Map();
  yield takeEvery(actions.performTransactionAction, function* performWithSkipsInFlight(action): SagaIterator {
    yield call(performTransactionAction, skipsInFlight, action);
  });
}

type EarliestDateOn = BaseResource & { dateOn?: string };

export function* getTransactionsEarliestDateOnActionWatcher() {
  const resourceConfig = createTypedQcsSyncResourcesConfigFactory<EarliestDateOn, undefined>()({
    resourceBaseUrl: '/transactions/earliest-date-on',
    successAction: actions.getTransactionsEarliestDateOnSucess,
    failureAction: actions.getTransactionsEarliestDateOnFailure,
    transformResourceToRequestData: () => ({ accountIds: [] }),
    transformResourceWithResponse: (_resource, response) => response.data,
    handleRetry: false,
    ignoreError: (error, _action, _config) => {
      verify(false, error);
      return true;
    },
  });
  yield takeEvery(
    actions.getTransactionsEarliestDateOn.type,
    resourceSagaUtils.qcsSyncCreateResource<EarliestDateOn, undefined>,
    resourceConfig,
  );
}

type genericResponse<T> = {
  config: any;
  data: T;
  headers: any;
  request: any;
  status: number;
  statusText: string;
};

export function* initiateA2ATransferActionWatcher() {
  yield takeEvery(
    actions.initiateA2ATransfer.type,
    function* handle(action: ReturnType<typeof actions.initiateA2ATransfer>) {
      try {
        const requestUrl = `${requireEnvironmentConfig().services_url}/transactions/submit-transfer`;
        const response: genericResponse<{ resources: TransactionProps[] }> = yield call(
          httpClientInterface.send,
          'PUT',
          requestUrl,
          {
            ...action.payload,
            amount: Number(action.payload?.amount).toFixed(2), // ensure decimal string
          },
        );
        log.log('initiate A2A Transfer response=', response);

        const resources: CashFlowTransaction[] = transformers.transformQcsTransactionsToTransactions(
          response.data?.resources,
        );

        yield put(actions.initiateA2ATransferSuccess(resources));
        yield put(a2aTransfersActions.getA2aTransfers());

        action.meta?.resolve?.(response);

        // give 2 seconds for service to update, then get the aikya-transfer resources
        yield delay(2000);
        yield put(a2aTransfersActions.getA2aTransfers());
      } catch (e: any) {
        log.warn('Error with A2A transfer request\n', e);
        yield put(actions.initiateA2ATransferFailure(e));
        action.meta?.reject?.(e);
      }
    },
  );
}

export default [
  getTransactionsActionWatcher,
  postRefreshTransactionsWatcher,
  getTransactionActionWatcher,
  createTransactionActionWatcher,
  updateTransactionActionWatcher,
  deleteTransactionActionWatcher,
  batchTransactionsActionWatcher,
  importAppleWalletTransactionsActionWatcher,
  performTransactionActionWatcher,
  getTransactionsEarliestDateOnActionWatcher,
  initiateA2ATransferActionWatcher,
];
